import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CarsProvider } from './context/CarsContext';
import { VoiceAssistantProvider, useVoiceAssistant } from './context/VoiceAssistantContext';
import { InlineChatHostProvider, useInlineChatHost } from './context/InlineChatHostContext';
import { ensureTenantFont } from './utils/tenantFont';
import { NotificationProvider } from './context/NotificationContext';
import { ServiceProvider } from './context/ServiceContext';
import { ContactParamsProvider } from './context/ContactParamsContext';
import { DutyFreeProvider } from './context/DutyFreeContext';
import { I18nProvider, useTranslation } from './i18n/I18nContext';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { VoiceWidgetConnected, getTheme } from './components/VoiceWidget';
import NotificationBanner from './components/NotificationBanner';
import HomePage from './pages/HomePage';
import DealerHomePage from './pages/DealerHomePage';
import CarsPage from './pages/CarsPage';
import CarDetailPage from './pages/CarDetailPage';
import CarPrintPage from './pages/CarPrintPage';
import CarPrintBulkPage from './pages/CarPrintBulkPage';
import CarLandingPage from './pages/CarLandingPage';
import ShowroomPage from './pages/ShowroomPage';
import SalonLandingPage from './pages/SalonLandingPage';
import AndysLandingConnected from './components/landing/andys/AndysLandingConnected';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import ShowroomsPage from './pages/ShowroomsPage';
import BookTestDrivePage from './pages/BookTestDrivePage';
import ServicePage from './pages/ServicePage';
import ServiceAppointmentPage from './pages/ServiceAppointmentPage';
import NotFoundPage from './pages/NotFoundPage';
import WidgetTestPage from './pages/WidgetTestPage';
import { config } from './config/runtimeConfig';
import { browserControlWS } from './services/browserControlWebSocket';
import { generateAppConfig } from './config/browserControlConfig';
import { useCommands } from './hooks/useCommands';
import { useNavigationTracking } from './hooks/useNavigationTracking';
import { useIframePostMessage } from './hooks/useIframePostMessage';
import { useCars } from './context/CarsContext';
import { useNotification } from './context/NotificationContext';
import { useIframeDetection } from './hooks/useIframeDetection';
import { useThemeCustomization } from './hooks/useThemeCustomization';
import { WidgetThemeControls } from './pages/shared/WidgetThemeControls';
import { cleanupExpiredHistory } from './services/messageHistory';
import { commandExecutor } from './services/commandExecutor';
import { isChatPinned } from './services/commandGenerator';
import { getShowRegText } from './utils/urlParams';
import './styles/theme-variables.css';
import './App.css';
import './styles/print.css';
import './app-shell.css';

/**
 * Показывать ли контрол внешнего вида ассистента.
 *
 * Выключен до следующего MR (#1330): темизацию виджета доводим отдельной
 * задачей, и полуготовый контрол витрине не нужен. Флаг, а не удаление кода:
 * всё остальное — state, `onChange`, проп `theme` у виджета — на месте и
 * работает, вернуть контрол можно одной правкой.
 */
const SHOW_WIDGET_THEME_CONTROLS = false;

function AppContent() {
  // Контейнер инлайн-чата, если текущая страница его зарегистрировала (#1330).
  const { host: inlineChatHost } = useInlineChatHost();
  // Язык витрины — в подписи виджета (#1417): поле чата на французской
  // витрине было «Type a message». Дилерский mount() языка не передаёт.
  const { language } = useTranslation();
  const { getAllCars } = useCars();
  const { showNotification } = useNotification();
  const { initialMode } = useVoiceAssistant();
  const isIframe = useIframeDetection();
  const location = useLocation();

  // Скрываем Header/Footer/Widget на print, landing и showroom страницах.
  // Showroom (#965) — отдельный flow с собственным минимальным header'ом
  // (только лого), без глобального nav и без футера.
  const isPrintPage = location.pathname.includes('/print');
  const isLandingPage = location.pathname.includes('/landing');
  // Точное совпадение, а НЕ `startsWith('/showroom')`: под префикс попадала
  // и витринная страница `/showrooms` (салоны, карта, QR) — её сносило в
  // chromeless-режим Autodis-флоу, и она оставалась без шапки и подвала.
  const isShowroomPage = location.pathname === '/showroom' || location.pathname.startsWith('/showroom/') || location.pathname.startsWith('/salon') || location.pathname.startsWith('/andys');
  // Andys landing has its own in-page "Ask AI" button in the footer; the
  // floating collapsed widget pill is hidden there (#1108).
  const isAndysPage = location.pathname.startsWith('/andys');
  const isChromeless = isPrintPage || isLandingPage || isShowroomPage;

  // The Andys landing hides the floating collapsed widget pill (it has its own
  // in-footer "Ask AI" button). The widget portals to document.body, so the flag
  // must live on <body>, not the app root (#1108).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.body.classList.toggle('sg-andys-page', isAndysPage);
    return () => document.body.classList.remove('sg-andys-page');
  }, [isAndysPage]);
  const isPrintOrLanding = isPrintPage || isLandingPage;

  // Применить кастомную тему из URL параметров или iframe data-атрибутов
  useThemeCustomization();

  // Установить флаг showRegText для использования в компонентах
  useEffect(() => {
    window.showRegText = getShowRegText();
    console.log('[App] Show reg text:', window.showRegText);
  }, []);

  // Отслеживать навигацию между страницами для отправки агенту
  useNavigationTracking();

  // Двусторонняя коммуникация с parent window (если работаем в iframe)
  useIframePostMessage();

  // Очистить устаревшие истории сообщений из localStorage при старте приложения
  useEffect(() => {
    console.log('[App] Cleaning up expired message history...');
    cleanupExpiredHistory();
  }, []); // Выполняется только один раз при монтировании


  // Обновляем app config с данными об автомобилях
  useEffect(() => {
    const cars = getAllCars();
    // Устанавливаем глобальную переменную для доступа из commandExecutor
    window.allCars = cars;
    const config = generateAppConfig(cars);
    browserControlWS.setAppConfig(config);
    console.log('[App] Browser control app config initialized with', config.metadata.totalCars, 'cars');
  }, [getAllCars]);

  // Регистрируем глобальную функцию для показа уведомлений о бронировании
  useEffect(() => {
    window.showSuccessBookingNotification = (data) => {
      console.log('[App] showSuccessBookingNotification called:', data);
      showNotification(data);
    };

    // Cleanup при размонтировании
    return () => {
      delete window.showSuccessBookingNotification;
    };
  }, [showNotification]);

  // Автоматически генерируем и обновляем высокоуровневые команды.
  // На странице чатов, где чат стоит блоком рядом с мокапом инбокса, команды,
  // уводящие с неё, агенту не выдаются: см. `isChatPinned` (#1330). Других
  // страниц это не касается.
  const chatPinned = isChatPinned(location.pathname, Boolean(inlineChatHost));
  useCommands({ pinned: chatPinned });

  // Тот же признак — исполнителю: набор команд агенту заменяется асинхронно, и
  // ход, начатый до входа на страницу, ещё держит прежний набор.
  useEffect(() => {
    commandExecutor.setPinned(chatPinned);
    return () => commandExecutor.setPinned(false);
  }, [chatPinned]);

  // Build widget theme в standalone-режиме (landing/print/каталог без widget.jsx
  // mount). showroomTokenResolver кладёт widgetStyle/Mode/Accent/Glass +
  // theme.{primaryColor,accentColor} в window.b2bMountParams; runtimeConfig
  // getters читают их с высшим приоритетом, getTheme собирает базу для
  // выбранного style/mode, дальше вручную накладываем primaryColor/accentColor
  // от dealership (Andys red, Autorai blue, ...) — иначе getTheme отдаст
  // preset-цвет style и кастомные colors из API не дойдут.
  // Включает реактивное чтение --theme-primary с documentElement (#965): когда
  // useDealerPreset('autodis') в ShowroomPage пишет inline-токен, MutationObserver
  // подхватывает изменение и widgetTheme пересчитывается с новым primaryColor —
  // иначе widget'овский inline-style игнорирует runtime-апдейт пресета и в чате
  // остаются дефолтные цвета style'a (orange demo-shift-gears).
  const [livePrimaryColor, setLivePrimaryColor] = useState(
    () => (typeof document !== 'undefined'
      ? document.documentElement.style.getPropertyValue('--theme-primary').trim() || null
      : null),
  );
  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return undefined;
    const read = () => {
      const v = document.documentElement.style.getPropertyValue('--theme-primary').trim() || null;
      setLivePrimaryColor((prev) => (prev === v ? prev : v));
    };
    // Coalesce mutation bursts via rAF — `pushThemeToDocumentElement`
    // sets 8 properties per preset apply, each firing its own mutation
    // event; without the rAF gate `read()` would run 8 times per tick
    // (state dedup still saves us from extra renders, but we'd hit
    // getPropertyValue 8x for nothing).
    let rafId = null;
    const obs = new MutationObserver(() => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        read();
      });
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    read();
    return () => {
      obs.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);
  // #1333 — плавающий контрол рядом с виджетом даёт посетителю витрины
  // покрутить внешний вид АССИСТЕНТА: бренд-пресет, светлая/тёмная тема,
  // яркий (accent) или белый фон коллапснутого состояния.
  //
  // Держим это обычным state и отдаём единственным пропом `theme` ниже —
  // никаких записей в `documentElement` и в mount-параметры. Первая версия
  // писала `--theme-primary` в inline-стиль корня, и вместе с виджетом
  // перекрашивалась вся витрина: кнопки, ссылки, карточки. Показывать надо,
  // что настраивается ассистент, а не что сайт умеет менять палитру.
  //
  // Стартуем от того, что отдаёт конфиг окружения, — контрол лишь смещает
  // значения от дефолта витрины, а не задаёт их с нуля.
  const [widgetLook, setWidgetLook] = useState(() => ({
    mode: config.WIDGET_THEME_MODE,
    accent: config.WIDGET_ACCENT,
    glass: config.WIDGET_GLASS,
  }));
  const handleWidgetLookChange = useCallback((patch) => {
    setWidgetLook((prev) => ({ ...prev, ...patch }));
  }, []);

  // Брендовый шрифт тенанта подтягиваем ровно тогда, когда витрина рисует его
  // поверхность (#1330). В `index.html` лежит только наш Source Sans 3.
  useEffect(() => {
    ensureTenantFont(config.WIDGET_STYLE);
  }, []);

  const widgetTheme = useMemo(() => {
    const baseTheme = getTheme(
      config.WIDGET_STYLE,
      widgetLook.mode,
      {
        accent: widgetLook.accent,
        glass: widgetLook.glass,
      }
    );
    const mountTheme = window.b2bMountParams?.theme || null;
    // Priority: live --theme-primary (dealer preset, runtime) > mountTheme
    // (set by showroomTokenResolver before React) > getTheme default.
    const primaryColor = livePrimaryColor || mountTheme?.primaryColor || baseTheme.primaryColor;
    const accentColor = mountTheme?.accentColor || baseTheme.accentColor;
    return { ...baseTheme, primaryColor, accentColor };
  }, [livePrimaryColor, widgetLook]);

  const routesEl = (
    <Routes>
      <Route
        path="/"
        element={config.IS_OWN_STOCK ? <HomePage /> : <DealerHomePage />}
      />
      <Route path="/cars" element={<CarsPage />} />
      <Route path="/cars/:id" element={<CarDetailPage />} />
      <Route path="/cars/:id/print" element={<CarPrintPage />} />
      <Route path="/cars/print-bulk" element={<CarPrintBulkPage />} />
      <Route path="/cars/:id/landing" element={<CarLandingPage />} />
      {/* Andys Buy·Rent·Lease landing (#1108) — preview/fallback на demo-
          домене. Основной surface — widget-view overlay на сайте дилера
          (WidgetViewOverlays). НЕ гейтим ENABLE_SHOWROOM_CATALOG (у Andys
          он false). */}
      <Route path="/andys" element={<AndysLandingConnected />} />
      {config.ENABLE_SHOWROOM_CATALOG && (
        <>
          <Route path="/showroom" element={<ShowroomPage />} />
          {/* /salon — welcome screen для общего QR салона (#1022).
              Парность с embed-путём (registry/Catalog) — чтобы standalone
              preview работал так же, как встроенный виджет. */}
          {/* На своей витрине салон — ShiftGears в светлой теме (#1441):
              приветственный экран Autodis с его пресетом и логотипом там
              чужой, и общий QR салона ведёт сразу в каталог шоурума. */}
          <Route
            path="/salon"
            element={
              config.SHOWROOM_BRAND === 'shiftgears' ? (
                <Navigate to="/showroom" replace />
              ) : (
                <SalonLandingPage />
              )
            }
          />
        </>
      )}
      {config.ENABLE_SERVICE_CENTER && (
        <>
          <Route path="/service" element={<ServicePage />} />
          <Route path="/service/appointment" element={<ServiceAppointmentPage />} />
        </>
      )}
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      {/* `/showrooms` во множественном числе: `/showroom` занят
          Autodis-флоу со свайп-каталогом за ENABLE_SHOWROOM_CATALOG. */}
      <Route path="/showrooms" element={<ShowroomsPage />} />
      <Route path="/book-test-drive" element={<BookTestDrivePage />} />
      <Route path="/widget-test" element={<WidgetTestPage />} />
      {/* Catch-all: до #1323 его не было, и незнакомый hash-путь не совпадал
          ни с одним Route — React Router не рендерил ничего, пользователь
          видел пустую полосу между шапкой и подвалом. Стоит ПОСЛЕДНИМ:
          `path="*"` совпадает с чем угодно и перекрыл бы всё, что ниже. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );

  return (
    // `app-shell--own-stock` — маркер витрины ShiftGears: на нём держатся все
    // стили редизайна #1335. Превью лендинга Andys (`/#/andys`) открывается
    // без токена, то есть тоже при IS_OWN_STOCK, но это дилерская
    // поверхность — витринные стили туда не идут.
    <div
      className={`app-shell${
        config.IS_OWN_STOCK && !isAndysPage ? ' app-shell--own-stock' : ''
      }`}
    >
      <ScrollToTop />
      {!isChromeless && <NotificationBanner />}
      {!isIframe && !isChromeless && <Header />}
      <main className="app-shell__main">
        {/* Витрина: страница проявляется при переходе (#1335) — без этого
            новая подменяла старую рывком. Только прозрачность, без сдвига:
            `transform` на предке сломал бы прилипающие `position: fixed`
            фильтры каталога. У дилера разметка прежняя, без обёртки. */}
        {config.IS_OWN_STOCK && !isAndysPage ? (
          <div key={location.pathname} className="app-shell__page">
            {routesEl}
          </div>
        ) : (
          routesEl
        )}
      </main>
      {!isIframe && !isChromeless && <Footer />}
      {/* Виджет ОДИН на всё приложение и никогда не размонтируется — на этом
          держится непрерывность разговора: агент умеет уводить сайт на другую
          страницу (`view_cars`, `set_filters`), и переписка обязана это
          пережить. Если страница зарегистрировала контейнер для инлайн-чата
          (#1330, пункт 7), тот же виджет портируется внутрь него; ушли со
          страницы — вернулся плавающим окном с той же историей.

          Второго экземпляра нет нигде намеренно: чат-embed'ы дерутся за
          синглтон `browserControlWS`, перезаписывая друг другу командный
          набор. Это причина #859. */}
      {!isPrintPage && (
        <VoiceWidgetConnected
          initialMode={inlineChatHost ? 'chat' : initialMode}
          language={language}
          theme={widgetTheme}
          inline={Boolean(inlineChatHost)}
          inlineHost={inlineChatHost}
          mirror={Boolean(inlineChatHost)}
        />
      )}
      {/* Контрол внешнего вида ассистента — только на витрине и только там, где
          виджет вообще показан (#1333). На chromeless-страницах (print, landing,
          showroom) своя минимальная оболочка, лишней плавающей иконке там не
          место.

          ⏸ СКРЫТ до следующего MR (#1330): темизацию доводим отдельно, и
          показывать полуготовый контрол на витрине незачем. Снят одним флагом,
          а не удалением: state `widgetLook` и проп `theme` у виджета живые,
          вернуть контрол — поменять `false` на `true`. */}
      {SHOW_WIDGET_THEME_CONTROLS && !isPrintPage && !isChromeless && (
        <WidgetThemeControls
          mode={widgetLook.mode}
          accent={widgetLook.accent}
          glass={widgetLook.glass}
          onChange={handleWidgetLookChange}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      {/* Только витрина переводит данные инвентаря (#1417) — и только на своём
          стоке: дилерский лендинг по старому QR с ?token открывается здесь
          же, и ему перевод не нужен (#1441). */}
      <I18nProvider translateInventory={config.IS_OWN_STOCK}>
        <ContactParamsProvider>
          {/* Выше AppContent: контейнер регистрирует блок внутри страницы, а
              читает его виджет, который рендерится рядом с <Routes>. */}
          <InlineChatHostProvider>
            <DutyFreeProvider>
            <CarsProvider>
              {config.ENABLE_SERVICE_CENTER ? (
                <ServiceProvider>
                  <VoiceAssistantProvider>
                    <NotificationProvider>
                      <AppContent />
                    </NotificationProvider>
                  </VoiceAssistantProvider>
                </ServiceProvider>
              ) : (
                <VoiceAssistantProvider>
                  <NotificationProvider>
                    <AppContent />
                  </NotificationProvider>
                </VoiceAssistantProvider>
              )}
            </CarsProvider>
            </DutyFreeProvider>
          </InlineChatHostProvider>
        </ContactParamsProvider>
      </I18nProvider>
    </Router>
  );
}

export default App;
